import { Button } from '@components/Button';
import { IconAdd } from '@components/icons';
import styles from './EmptyState.module.scss';

export interface EmptyStateProps {
  /** Icon or illustration to display */
  icon?: React.ReactNode;

  /** Main heading text */
  title?: string;

  /** Descriptive subtitle text */
  subtitle?: string;

  /** Size variant: 'default' (large with buttons) or 'minimal' (compact, no buttons) */
  size?: 'default' | 'minimal';

  /** Primary action button text */
  primaryActionText?: string;

  /** Primary action click handler */
  onPrimaryAction?: () => void;

  /** Primary action leading icon (defaults to IconAdd) */
  primaryActionLeadingIcon?: React.ReactNode;

  /** Primary action trailing icon */
  primaryActionTrailingIcon?: React.ReactNode;

  /** Hide default leading icon on primary action */
  hidePrimaryActionLeadingIcon?: boolean;

  /** Secondary action button text */
  secondaryActionText?: string;

  /** Secondary action click handler */
  onSecondaryAction?: () => void;

  /** Show secondary action button */
  showSecondaryAction?: boolean;

  /** Additional CSS classes */
  className?: string;
}

export const EmptyState = ({
  icon,
  title,
  subtitle,
  size = 'default',
  primaryActionText,
  onPrimaryAction,
  primaryActionLeadingIcon,
  primaryActionTrailingIcon,
  hidePrimaryActionLeadingIcon = false,
  secondaryActionText,
  onSecondaryAction,
  showSecondaryAction = false,
  className = '',
}: EmptyStateProps) => {
  const isMinimal = size === 'minimal';

  const containerClassName = [
    styles.container,
    isMinimal && styles.minimal,
    className,
  ].filter(Boolean).join(' ');

  // Determine the leading icon for the primary action
  const leadingIcon = hidePrimaryActionLeadingIcon
    ? undefined
    : primaryActionLeadingIcon ?? <IconAdd size={18} />;

  return (
    <div className={containerClassName}>
      {/* Icon Container */}
      {icon && <div className={styles.icon}>{icon}</div>}

      {/* Content Container */}
      {(title || subtitle) && (
        <div className={styles.content}>
          {title && <h3 className={styles.title}>{title}</h3>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}

      {/* Actions Container - hidden in minimal mode */}
      {!isMinimal && (primaryActionText || showSecondaryAction) && (
        <div className={styles.actions}>
          {/* Primary Action */}
          {primaryActionText && (
            <Button
              variant="primary"
              size="regular"
              leadingIcon={leadingIcon}
              trailingIcon={primaryActionTrailingIcon}
              onClick={onPrimaryAction}
            >
              {primaryActionText}
            </Button>
          )}

          {/* Secondary Action */}
          {showSecondaryAction && (
            <>
              <span className={styles.divider}>OR</span>
              <Button variant="text" size="regular" onClick={onSecondaryAction}>
                {secondaryActionText || 'Secondary Action'}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
